import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { Duration } from 'luxon';
import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { AzurePostgreSqlCopySource } from 'app/model/pipeline/copysource/azure-postgresql.copysource';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzurePostgreSqlCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        query: '*',
        queryTimeout: '*',
        partitionOption: '*',
        partitionSettings: {
            partitionColumnName: '*',
            partitionUpperBound: '*',
            partitionLowerBound: '*',
            partitionNames: '*'
        }
    }
})
export class AzurePostgreSqlCopySourceAdapter extends CopySourceAdapter<
    AzurePostgreSqlCopySource,
    Model.AzurePostgreSqlSource
> {
    protected override async _toResource(copySource: AzurePostgreSqlCopySource): Promise<Model.AzurePostgreSqlSource> {
        const result: Model.AzurePostgreSqlSource = this._toTabularResource(
            copySource,
            Metamodel.modelFor(AzurePostgreSqlCopySource).name
        );
        result.partitionOption = ValueAdapter.toResource(copySource, AzurePostgreSqlCopySource.PARTITION_OPTION);
        if (AzurePostgreSqlCopySource.PARTITION_OPTION.templateValue(copySource) != null) {
            result.partitionSettings = {
                partitionColumnName: ValueAdapter.toResource(
                    copySource,
                    AzurePostgreSqlCopySource.PARTITION_COLUMN_NAME
                ),
                partitionUpperBound: ValueAdapter.toResource(
                    copySource,
                    AzurePostgreSqlCopySource.PARTITION_UPPER_BOUND
                ),
                partitionLowerBound: ValueAdapter.toResource(
                    copySource,
                    AzurePostgreSqlCopySource.PARTITION_LOWER_BOUND
                ),
                partitionNames: ValueAdapter.toResource(copySource, AzurePostgreSqlCopySource.PARTITION_NAMES)
            };
        } else if (
            !stringIsNullOrEmpty(copySource.partitionOption) &&
            copySource.partitionOption !== partitionOptionConst.none
        ) {
            if (copySource.partitionOption === partitionOptionConst.dyamicRange) {
                result.partitionSettings = {
                    partitionColumnName: ValueAdapter.toResource(
                        copySource,
                        AzurePostgreSqlCopySource.PARTITION_COLUMN_NAME
                    ),
                    partitionUpperBound: ValueAdapter.toResource(
                        copySource,
                        AzurePostgreSqlCopySource.PARTITION_UPPER_BOUND
                    ),
                    partitionLowerBound: ValueAdapter.toResource(
                        copySource,
                        AzurePostgreSqlCopySource.PARTITION_LOWER_BOUND
                    )
                };
            } else if (copySource.partitionOption === partitionOptionConst.physicalPartitionsOfTable) {
                result.partitionSettings = {
                    partitionNames: ValueAdapter.toResource(copySource, AzurePostgreSqlCopySource.PARTITION_NAMES)
                };
            }
        }

        try {
            result.queryTimeout =
                typeof copySource.queryTimeout === 'number'
                    ? Duration.fromObject({ minutes: copySource.queryTimeout }).toFormat('hh:mm:ss')
                    : ValueAdapter.toResource(copySource, AzurePostgreSqlCopySource.QUERY_TIMEOUT);
        } catch {
            this._logError('Failed to convert AzurePostgreSqlCopySource queryTimeout into TimeSpan');
        }
        return result;
    }

    protected override async _toModel(
        resource: Model.AzurePostgreSqlSource,
        copyActivity: CopyActivity
    ): Promise<AzurePostgreSqlCopySource> {
        const result: AzurePostgreSqlCopySource = this._toTabularModel(
            resource,
            copyActivity,
            AzurePostgreSqlCopySource.MODEL
        );
        if (resource.partitionOption !== undefined) {
            ValueAdapter.toModel(result, AzurePostgreSqlCopySource.PARTITION_OPTION, resource.partitionOption);
        } else {
            delete result.partitionOption;
        }
        if (resource.partitionSettings != null) {
            ValueAdapter.toModel(
                result,
                AzurePostgreSqlCopySource.PARTITION_NAMES,
                resource.partitionSettings.partitionNames
            );
            ValueAdapter.toModel(
                result,
                AzurePostgreSqlCopySource.PARTITION_COLUMN_NAME,
                resource.partitionSettings.partitionColumnName
            );
            ValueAdapter.toModel(
                result,
                AzurePostgreSqlCopySource.PARTITION_LOWER_BOUND,
                resource.partitionSettings.partitionLowerBound
            );
            ValueAdapter.toModel(
                result,
                AzurePostgreSqlCopySource.PARTITION_UPPER_BOUND,
                resource.partitionSettings.partitionUpperBound
            );
        }

        try {
            if (resource.queryTimeout == null) {
                result.queryTimeout = undefined;
            } else {
                if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                    const parts = resource.queryTimeout.split(':');
                    if (parts.length === 3) {
                        const duration = Duration.fromObject({
                            hours: parseInt(parts[0], 10),
                            minutes: parseInt(parts[1], 10),
                            seconds: parseInt(parts[2], 10)
                        });
                        result.queryTimeout = duration.as('minutes');
                    } else {
                        this._logError('Failed to convert AzurePostgreSqlCopySource queryTimeout into number');
                    }
                } else {
                    ValueAdapter.toModel(result, AzurePostgreSqlCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                }
            }
        } catch {
            this._logError('Failed to convert AzurePostgreSqlCopySource queryTimeout into number');
        }
        return result;
    }
}
